import {
  DepartmentStateTab,
  DepartmentTreeItem,
  DepartmentTreeNode,
  OrgChartPosition,
  OrgChartPositionNode,
} from '../department-model';

/** Resolves a server-computed `reports_to_node_key` (`position:{id}` / `work_info:{id}`) into the synthetic numeric id `buildOrgChartPositions` assigns its nodes. */
function resolveNodeKeyToId(key: string | null | undefined): number | null {
  if (!key) {
    return null;
  }
  const [kind, idPart] = key.split(':');
  const id = Number(idPart);
  if (!Number.isFinite(id)) {
    return null;
  }
  return kind === 'work_info' ? -id : id;
}

/**
 * Merges a department's real Positions with its "Department and Job Title" placed
 * users (`work_info_users`) into a single `OrgChartPosition[]`, so the existing
 * `buildPositionForests`/org-chart rendering needs no changes to show both. Work-info
 * users get a negative synthetic id (work info and position ids are both globally
 * unique, so negating avoids collisions) and their `reports_to_position_id` is
 * resolved from the server-computed `reports_to_node_key` on both kinds of node.
 */
export function buildOrgChartPositions(
  department: DepartmentTreeItem
): OrgChartPosition[] {
  const positions = department.positions.map(position => ({
    ...position,
    reports_to_position_id:
      resolveNodeKeyToId(position.reports_to_node_key) ??
      position.reports_to_position_id,
  }));

  const workInfoPositions: OrgChartPosition[] = (
    department.work_info_users ?? []
  ).map(workInfoUser => ({
    id: -workInfoUser.id,
    department_id: workInfoUser.department_id,
    reports_to_position_id: resolveNodeKeyToId(
      workInfoUser.reports_to_node_key
    ),
    name: workInfoUser.job_title?.name ?? workInfoUser.user?.name ?? '',
    job_title: workInfoUser.job_title,
    state: null,
    incumbent: workInfoUser.user,
  }));

  return [...positions, ...workInfoPositions];
}

export interface DepartmentTreeFilter {
  tab: DepartmentStateTab;
  search: string;
}

export interface DepartmentRow {
  id: number;
  department: DepartmentTreeItem;
  depth: number;
  isMatch: boolean;
  isExpanded: boolean;
  hasChildren: boolean;
  subDepartmentsCount: number;
  ownPositionsCount: number;
  totalPositionsCount: number;
}

export function buildDepartmentTree(
  items: DepartmentTreeItem[]
): DepartmentTreeNode[] {
  const nodesById = new Map<number, DepartmentTreeNode>();
  for (const department of items) {
    nodesById.set(department.id, {
      department,
      children: [],
      depth: 0,
      isMatch: true,
      ownPositionsCount: department.positions_count,
      totalPositionsCount: department.positions_count,
    });
  }

  const roots: DepartmentTreeNode[] = [];
  for (const node of nodesById.values()) {
    const parentId = node.department.parent_id;
    const parent = parentId === null ? undefined : nodesById.get(parentId);
    if (parent) {
      parent.children.push(node);
    } else {
      roots.push(node);
    }
  }

  const finalize = (node: DepartmentTreeNode, depth: number): number => {
    node.depth = depth;
    node.totalPositionsCount =
      node.ownPositionsCount +
      node.children.reduce((sum, child) => sum + finalize(child, depth + 1), 0);
    return node.totalPositionsCount;
  };
  roots.forEach(root => finalize(root, 0));

  return roots;
}

export function departmentMatches(
  department: DepartmentTreeItem,
  filter: DepartmentTreeFilter
): boolean {
  if (filter.tab !== 'all' && department.state?.value !== filter.tab) {
    return false;
  }

  const search = filter.search.trim().toLocaleLowerCase();
  if (!search) {
    return true;
  }

  return [
    department.translations?.name?.en,
    department.translations?.name?.ar,
    department.name,
    department.identifier,
  ].some(value => value?.toLocaleLowerCase().includes(search));
}

export function filterDepartmentTree(
  nodes: DepartmentTreeNode[],
  filter: DepartmentTreeFilter
): DepartmentTreeNode[] {
  return nodes.flatMap(node => {
    const children = filterDepartmentTree(node.children, filter);
    const isMatch = departmentMatches(node.department, filter);
    return isMatch || children.length ? [{ ...node, children, isMatch }] : [];
  });
}

export function countMatches(nodes: DepartmentTreeNode[]): number {
  return nodes.reduce(
    (sum, node) => sum + (node.isMatch ? 1 : 0) + countMatches(node.children),
    0
  );
}

export function flattenDepartmentRows(
  nodes: DepartmentTreeNode[],
  expandedIds: ReadonlySet<number>,
  expandAll: boolean
): DepartmentRow[] {
  return nodes.flatMap(node => {
    const hasChildren = node.children.length > 0;
    const isExpanded =
      hasChildren && (expandAll || expandedIds.has(node.department.id));
    const row: DepartmentRow = {
      id: node.department.id,
      department: node.department,
      depth: node.depth,
      isMatch: node.isMatch,
      isExpanded,
      hasChildren,
      subDepartmentsCount: node.children.length,
      ownPositionsCount: node.ownPositionsCount,
      totalPositionsCount: node.totalPositionsCount,
    };
    return isExpanded
      ? [row, ...flattenDepartmentRows(node.children, expandedIds, expandAll)]
      : [row];
  });
}

export function expandableDepartmentIds(
  nodes: DepartmentTreeNode[]
): Set<number> {
  const ids = new Set<number>();
  const walk = (node: DepartmentTreeNode): void => {
    if (node.children.length) {
      ids.add(node.department.id);
      node.children.forEach(walk);
    }
  };
  nodes.forEach(walk);
  return ids;
}

export function expandableOrgChartIds(
  nodes: DepartmentTreeNode[],
  positionForests: ReadonlyMap<number, OrgChartPositionNode[]>
): Set<number> {
  const ids = new Set<number>();
  const walk = (node: DepartmentTreeNode): void => {
    if (
      node.children.length ||
      (positionForests.get(node.department.id)?.length ?? 0) > 0
    ) {
      ids.add(node.department.id);
    }
    node.children.forEach(walk);
  };
  nodes.forEach(walk);
  return ids;
}

export function buildPositionForests(
  positions: OrgChartPosition[]
): Map<number, OrgChartPositionNode[]> {
  const nodesById = new Map<number, OrgChartPositionNode>(
    positions.map(position => [position.id, { position, children: [] }])
  );
  const forests = new Map<number, OrgChartPositionNode[]>();

  for (const node of nodesById.values()) {
    const departmentId = node.position.department_id;
    if (departmentId === null) {
      continue;
    }

    const managerId = node.position.reports_to_position_id;
    const manager = managerId === null ? undefined : nodesById.get(managerId);
    if (manager && manager.position.department_id === departmentId) {
      manager.children.push(node);
      continue;
    }

    const tops = forests.get(departmentId);
    if (tops) {
      tops.push(node);
    } else {
      forests.set(departmentId, [node]);
    }
  }

  return forests;
}
